import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { useRef } from 'react'
import { gsap, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { SplitChars } from '@/components/motion/split-text'
import Button from '@/components/generals/button'
import Img from '@/components/generals/img'
import { editorial } from '@/constants/media'

interface Look {
    src: string
    alt: string
    label: string
    chapter: string
}

const LEFT: Array<Look> = [
    { src: editorial.fogCityWalk, alt: "A figure walking through a foggy street streaked with city lights", label: '02:10', chapter: 'II — Night Shift' },
    { src: editorial.neonDoorway, alt: "A figure in a long coat passing a glowing neon doorway", label: '02:45', chapter: 'II — Night Shift' },
    { src: editorial.rainOnGlass, alt: "Walkers on a wet pavement seen through rain on glass", label: '03:20', chapter: 'II — Night Shift' }
]

const RIGHT: Array<Look> = [
    { src: editorial.coatPastShopfront, alt: "A woman in a black coat striding past a lit shopfront", label: '03:55', chapter: 'II — Night Shift' },
    { src: editorial.coatAndCapShadow, alt: "A figure in a long coat and cap casting a hard shadow", label: '04:30', chapter: 'II — Night Shift' },
    { src: editorial.wetAvenue, alt: "A rain-soaked avenue with blurred headlights and walkers", label: '04:50', chapter: 'II — Night Shift' }
]

interface ColumnProps {
    items: Array<Look>
    side: 'left' | 'right'
}

const Column: FC<ColumnProps> = ({ items, side }): ReactNode => {
    return (
        <div data-column={side} className='flex flex-col gap-6 md:gap-8'>
            {items.map((item, index) => (
                <figure key={item.src} className={index === 1 ? 'md:w-[82%] md:self-end' : undefined}>
                    <Link
                        href='/lookbook'
                        data-cursor='Enter'
                        aria-label={`${item.label}, open the lookbook`}
                        className='group relative block aspect-[3/4] overflow-hidden bg-coal'
                    >
                        <Img
                            src={item.src}
                            alt={item.alt}
                            sizes='(min-width: 768px) 24vw, 45vw'
                            className='transition-transform duration-[1.4s] ease-expo-out group-hover:scale-105'
                        />
                    </Link>

                    <figcaption className='mt-3 flex flex-wrap justify-between gap-x-3 meta-mono text-ash'>
                        <span>
                            {item.label}
                        </span>

                        <span>
                            {item.chapter}
                        </span>
                    </figcaption>
                </figure>
            ))}
        </div>
    )
}

/** Two image columns drifting at different speeds around a pinned chapter title. */
const SplitLookbook: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useGSAP(() => {
        if (!root.current || prefersReducedMotion()) return

        const media = gsap.matchMedia()

        media.add('(min-width: 768px)', () => {
            const scrollTrigger = { trigger: root.current, start: 'top bottom', end: 'bottom top', scrub: true }

            gsap.fromTo('[data-column="left"]', { yPercent: 6 }, { yPercent: -16, ease: 'none', scrollTrigger })
            gsap.fromTo('[data-column="right"]', { yPercent: -10 }, { yPercent: 10, ease: 'none', scrollTrigger })
        })
    }, { scope: root })

    return (
        <section id='lookbook-teaser' ref={root} aria-label='Lookbook, chapter two' className='relative overflow-hidden bg-ink py-24 md:py-40'>
            <div className='grid grid-cols-2 gap-x-4 gap-y-14 gutter md:grid-cols-12 md:gap-6'>
                <div className='order-2 md:order-1 md:col-span-3'>
                    <Column items={LEFT} side='left' />
                </div>

                <div className='order-1 col-span-2 md:sticky md:top-0 md:order-2 md:col-span-6 md:flex md:h-[100svh] md:flex-col md:items-center md:justify-center md:self-start md:text-center'>
                    <p data-reveal='fade' className='label-mono text-acid'>
                        (04) — Lookbook
                    </p>

                    <h2 data-reveal='chars' className='mt-6 font-heading text-[clamp(72px,10vw,168px)] leading-[0.82] text-bone'>
                        <span className='block'>
                            <SplitChars text='Night' />
                        </span>

                        <span className='-mt-1 block font-serif text-[clamp(64px,8.8vw,148px)] italic leading-[0.9] tracking-[-0.02em] text-acid'>
                            <SplitChars text='shift' />
                        </span>
                    </h2>

                    <p data-reveal='fade' className='mt-8 max-w-[38ch] body-copy text-bone/75'>
                        Chapter II of the SS26 campaign — shot between 2 and 5 a.m. on the empty streets of Berlin-Mitte.
                    </p>

                    <div data-reveal='fade' className='mt-8'>
                        <Button href='/lookbook' variant='ghost'>
                            Enter the lookbook
                        </Button>
                    </div>
                </div>

                <div className='order-3 pt-16 md:col-span-3 md:pt-[38vh]'>
                    <Column items={RIGHT} side='right' />
                </div>
            </div>
        </section>
    )
}

export default SplitLookbook
